import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { IBudgetItem } from '../../blueprint';
import removeImage from './remove.svg';
import textCss from '../../../../../components/form/elements/text.module.css';
import css from '../../index.module.css';

interface Props {
  item: IBudgetItem;
  dataName?: string;
  remove?: (item: IBudgetItem) => void;
}

export default function TableRow({ item, dataName, remove }: Props) {
  const { t } = useTranslation();
  const formMethods = useFormContext();
  const { register, setValue, getValues } = formMethods;
  const [isDisabled, setIsDisabled] = useState(false);
  const styles = useMemo(() => {
    return css['table-row'].concat(
      ' ',
      css[`row-lvl-${item.level}`],
      ' ',
      item.uppercase ? 'uppercase' : '',
      ' ',
      item.hideBorder ? css['no-border'] : ''
    );
  }, [item]);

  const handleChange = useCallback(
    (event: any) => {
      if (event.target.value && event.target.value.length) {
        const inputValue = Number(event.target.value || 0);
        const formattedValue = parseFloat(inputValue.toFixed(2));
        if (
          getValues(item.name) !== formattedValue ||
          inputValue !== formattedValue
        ) {
          setValue(item.name, formattedValue);
        }
      } else {
        setValue(item.name, 0);
      }
    },
    [item.name, getValues, setValue]
  );

  const watchValues = useWatch({
    name: item.calculable,
    disabled: !item.calculable,
  });

  useEffect(() => {
    function setSum(sum: number) {
      sum = parseFloat(sum.toFixed(2));
      if (getValues(item.name) !== sum) {
        if (sum !== 0) {
          setValue(item.name, sum);
          item.value = sum;
        } else {
          setValue(item.name, 0);
          item.value = 0;
        }
      }
      setIsDisabled(sum !== 0);
    }

    if (watchValues && watchValues.length) {
      const sum = watchValues.reduce((accumulator, currentValue) => {
        if (
          typeof currentValue === 'number' ||
          typeof currentValue === 'string'
        ) {
          return parseFloat((accumulator + Number(currentValue)).toFixed(2));
        }
        return accumulator;
      }, 0);
      setSum(sum);
    } else {
      if (!Object.values(watchValues).length) {
        if (getValues(item.name) !== 0) {
          setSum(0);
        }
      }
    }
  }, [item.name, setValue, getValues, watchValues, isDisabled, item]);

  return (
    <div className={styles} data-name={dataName}>
      <div className={css['table-data']}>{t(item.title)}</div>

      <div className={css['table-data']}>
        {item.calculable ? (
          <div className={textCss['input-container']}>
            <input
              type="number"
              name={item.name}
              data-name={item.name + '_input'}
              className={textCss['input']}
              {...register(item.name)}
              defaultValue={item.value}
              disabled={true}
            />
          </div>
        ) : (
          <div className={textCss['input-container']}>
            <input
              type="number"
              name={item.name}
              data-name={item.name + '_input'}
              className={textCss['input']}
              min="0"
              step=".01"
              {...register(item.name)}
              defaultValue={item.value || 0}
              disabled={isDisabled}
              onChange={handleChange}
            />
          </div>
        )}
      </div>

      <div className={css['table-data']}>
        {remove ? (
          <div className={css['remove-btn']} onClick={() => remove(item)}>
            <img src={removeImage} alt="" />
          </div>
        ) : null}
      </div>
    </div>
  );
}
